/**
 * prompt.ts —— 系统提示词组装
 *
 * 架构原则 2 说「能力清单只有一个来源」：提示词里的「我能干什么」
 * 必须由 Host Registry 的 describeForPrompt() 自动生成，
 * 不许人肉维护第二份文档。
 *
 * 但 M2 时 Registry 还没建（M3 才有）。所以这里做了一个过渡设计：
 *   `buildSystemPrompt(capabilityList)` 接受一段能力清单文本，
 *   调用方负责提供。M3 接上 Registry 后，调用方改成传
 *   `registry.describeForPrompt()` 即可，本文件不用动。
 *
 * 这样做的额外收益：提示词的「规则部分」和「能力部分」是分开的，
 * 规则不会因为加 API 而改动 —— 正是架构原则 1 想要的解耦。
 */

/**
 * 规则部分 —— 与能力清单无关，只写「怎么说话」。
 *
 * 每一段都在压制模型的一个具体的坏习惯，不是泛泛的「请遵守规则」：
 *   · 说「好的，我来帮你」  → 要求直接输出代码
 *   · 加 Markdown 围栏      → 明确禁止
 *   · 预言执行结果          → 信任原则的核心（D7）
 *   · 编造不存在的 API      → 要求只用清单里的
 */
const RULES = `你是一个把用户的自然语言翻译成 IL 指令的翻译器。IL 是本应用自有的迷你指令语言。

【唯一任务】
把用户的话翻译成 IL 指令。你只做翻译，不做别的。

【输出格式】
只输出 IL 代码本身，不要任何其他内容。具体来说：
· 不要输出 Markdown 代码围栏（不要 \`\`\`）
· 不要输出解释、前言、后记
· 不要输出序号、列表符号
· 多条指令用分号 ; 分隔，可以写成多行
· 可以在 \`#\` 后写注释，但注释不是必需的，能省则省

【最重要的规则：不许预言结果】
你在翻译阶段不知道指令执行成功了没有。所以：
· 绝对不要在输出里描述执行结果
· 绝对不要写「已切换」「已完成」「正在为你」这类话
· 执行成功后的提示语由程序自动生成，不需要你操心
· 你唯一要做的就是把用户的意图翻译成指令

【做不到的时候】
如果用户的请求无法用清单里的能力完成，输出：
  explain('说明为什么做不到，以及可以怎么做')
普通聊天、知识问答、闲聊等非操作性请求，也用 explain 来回答：
  explain('这里是你的回答')
explain 是你唯一可以「只说话不动手」的出口，它没有任何副作用。

【只能用清单里的能力】
下面列出的能力是这个应用的全部能力。不要使用清单以外的任何方法名 ——
它们不存在，用了必然失败。如果用户要求的功能不在清单里，用 explain 说明。

【参数规则】
· 字符串用单引号或双引号包裹，推荐单引号
· 用户说的话作为参数时，原样放进引号里，不要改写、不要总结`;

/**
 * 组装完整的系统提示词。
 *
 * @param capabilityList 能力清单文本（由 registry.describeForPrompt() 生成）
 * @param handleList     当前对话里可引用的句柄清单（可选，为空则省略这一节）
 */
export function buildSystemPrompt(capabilityList: string, handleList = ''): string {
  const capabilities = capabilityList.trim() || '（当前没有可用能力）';
  const handles = handleList.trim();

  const handleSection = handles
    ? `

【当前可引用的对象】
这些是你刚才的操作产生的、现在还能引用的东西。需要用到它们时把编号原样填进参数：
${handles}`
    : '';

  return `${RULES}

【当前应用的全部能力】
${capabilities}${handleSection}`;
}

/**
 * 失败二轮反馈时追加的提示词（D10）。
 *
 * 场景：指令执行失败了，把真实的错误信息回喂给模型，
 * 让它用自然语言解释发生了什么 —— 这时模型**有**执行结果的知情权，
 * 所以允许它描述结果。
 *
 * 注意与 RULES 的区别：那一轮禁止描述结果，这一轮要求描述结果。
 * 这正是信任原则的精细之处 —— 知情权决定了措辞权。
 */
export const FEEDBACK_RULES = `上一条指令在执行时失败了。下面是真实的错误信息。

现在你**已经知道**执行结果了，所以可以（也应该）向用户说明情况。请：
· 用 explain('...') 输出一段给用户看的话
· 说明哪里出了问题，用用户能听懂的语言
· 如果可能，给出一个替代方案
· 不要道歉过度，不要重复错误的技术细节，直接说人话`;

/**
 * 「回答轮」追加的提示词。
 *
 * 场景：用户问的是「有哪些联系人」「我和谁聊过」这类**提问**，
 * 模型给出了查询指令（contact.list 这种带 [查] 标记的方法），
 * 程序真的执行了、也真的取到了数据 —— 但数据只存在于返回值里，
 * 用户什么都没看到（"已读取联系人列表" 不是回答）。
 *
 * 于是走这一轮：把**真实取到的数据**回喂给模型，让它用自然语言回答。
 *
 * 与失败二轮的关键区别：
 *   · 这里回喂的不是错误，而是成功取到的数据
 *   · 这一轮的产出同样**不执行**（见 feedback.ts），只当文本展示，
 *     因为它应该是 explain('...')，是给用户看的话
 *
 * 为什么不把数据直接渲染给用户、而要绕一圈给 AI：
 *   因为数据是结构化的（一串对象），直接展示会很生硬；
 *   而这个应用的产品命题就是「AI 是通用控制层」——
 *   把结构化数据转成人话，正是它该做的事。
 *   同时这也守住了信任原则：AI 说的是**真实数据**，不是它猜的。
 */
export const ANSWER_RULES = `刚才的查询指令已经执行成功，下面是**真实**取到的数据。

现在请把这些数据变成一句给用户看的回答。要求：
· 用 explain('...') 输出
· 直接回答用户的问题，不要说「已读取」「已获取」这类过程性的话
· 数据是给人看的，念清楚关键的几项（人名、条数、时间等）
· 如果数据是空的，就如实说没有
· 不要编造数据里没有的内容
· 简洁，别罗列原始结构`;

/**
 * 组装「回答轮」的完整提示词。
 *
 * @param capabilityList 同 buildSystemPrompt
 * @param originalCode   模型上一轮给出的查询指令
 * @param dataText       真实取到的数据（由调用方渲染成文本）
 */
export function buildAnswerPrompt(
  capabilityList: string,
  originalCode: string,
  dataText: string,
  handleList = '',
): { systemPrompt: string; userMessage: string } {
  return {
    systemPrompt: buildSystemPrompt(capabilityList, handleList),
    userMessage: `${ANSWER_RULES}

【刚才的查询指令】
${originalCode}

【真实取到的数据】
${dataText}`,
  };
}

/**
 * 组装失败二轮的完整提示词。
 *
 * @param capabilityList 同 buildSystemPrompt
 * @param originalCode   失败的那段 IL
 * @param errorText      解释器给出的真实错误
 */
export function buildFeedbackPrompt(
  capabilityList: string,
  originalCode: string,
  errorText: string,
  handleList = '',
): { systemPrompt: string; userMessage: string } {
  return {
    systemPrompt: buildSystemPrompt(capabilityList, handleList),
    userMessage: `${FEEDBACK_RULES}

【失败的指令】
${originalCode}

【真实错误】
${errorText}`,
  };
}
